/**
 * ExitSurveyModal
 *
 * Multi-step modal that collects an exit survey before confirming account
 * deletion. Delegates final deletion to parent via `onDeleteAccount`.
 */
import React, { useState, useEffect } from 'react';
import { X, ArrowRight, AlertTriangle } from 'lucide-react';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';

export function ExitSurveyModal({
  isOpen,
  onClose,
  onDeleteAccount,
  onCancelSubscription,
  isLoading = false,
  error = null
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [surveyData, setSurveyData] = useState({
    reason: '',
    improvements: [],
    feedback: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reasons for account deletion
  const reasonOptions = [
    'Too expensive',
    'Did not find much use for it',
    'Found a better alternative',
    'Difficult to use',
    'Not enough templates',
    'Missing features I need',
    'Just testing the service',
    'Other'
  ];

  // Improvement options 
  const improvementOptions = [
    'More templates',
    'Better customer service',
    'More features',
    'Easier to use interface',
    'Lower pricing',
    'Better mobile experience',
    'More customization options',
    'Faster performance'
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      // Reset state when modal closes
      setCurrentPage(0);
      setSurveyData({
        reason: '',
        improvements: [],
        feedback: ''
      });
      setErrors({});
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const validateCurrentPage = () => {
    const newErrors = {};
    
    if (currentPage === 0 && !surveyData.reason) {
      newErrors.reason = 'Please select a reason';
    }
    
    if (currentPage === 1 && surveyData.improvements.length === 0) {
      newErrors.improvements = 'Please select at least one improvement';
    }
    
    if (currentPage === 2 && !surveyData.feedback.trim()) {
      newErrors.feedback = 'Please provide some feedback';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isCurrentPageValid = () => {
    if (currentPage === 0) {
      return !!surveyData.reason;
    }
    if (currentPage === 1) {
      return surveyData.improvements.length > 0;
    }
    if (currentPage === 2) {
      return !!surveyData.feedback.trim();
    }
    return true;
  };

  const handleNextPage = () => {
    if (validateCurrentPage()) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const handleReasonChange = (reason) => {
    setSurveyData({ ...surveyData, reason });
    setErrors({ ...errors, reason: undefined });
  };

  const handleImprovementToggle = (improvement) => {
    const updatedImprovements = surveyData.improvements.includes(improvement)
      ? surveyData.improvements.filter(item => item !== improvement)
      : [...surveyData.improvements, improvement];
    
    setSurveyData({ ...surveyData, improvements: updatedImprovements });
    
    if (updatedImprovements.length > 0) {
      setErrors({ ...errors, improvements: undefined });
    }
  };

  const handleFeedbackChange = (e) => {
    setSurveyData({ ...surveyData, feedback: e.target.value });
    
    if (e.target.value.trim()) {
      setErrors({ ...errors, feedback: undefined });
    }
  };

  const handleSubmit = async () => {
    if (validateCurrentPage()) {
      setIsSubmitting(true);
      try {
        // Pass the survey data directly to the delete account function
        // This will include it in the deletion request to capture it in the deleted user record
        await onDeleteAccount(surveyData);
      } catch (error) {
        console.error('Error submitting exit survey:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Use either the internal state or the prop passed from parent
  const isButtonLoading = isSubmitting || isLoading;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={isButtonLoading ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900 sm:my-8">
        {/* Close button - Fixed position and z-index */}
        {!isButtonLoading && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Progress indicator */}
        <div className="flex w-full">
          {[0, 1, 2, 3].map((step) => (
            <div 
              key={step}
              className={`h-1 flex-1 transition-colors duration-300 ${
                step <= currentPage ? 'bg-purple-600 dark:bg-purple-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        <div className="px-6 py-6">
          {/* Page 1: Reason for leaving */}
          {currentPage === 0 && (
            <div className="animate-fadeIn">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                We're sorry to see you go
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                What's the primary reason you're deleting your account?
              </p>

              {onCancelSubscription && (
                <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/20">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Just want to stop billing? You can cancel your subscription instead — your
                    account and all your work stay intact.
                  </p>
                  <button
                    type="button"
                    onClick={onCancelSubscription}
                    className="mt-2 text-sm font-medium text-purple-700 hover:text-purple-900 dark:text-purple-300 dark:hover:text-purple-200"
                  >
                    Cancel subscription instead →
                  </button>
                </div>
              )}

              <div className="mt-4 space-y-2">
                {reasonOptions.map((reason) => (
                  <button 
                    key={reason}
                    type="button"
                    onClick={() => handleReasonChange(reason)}
                    className={`w-full text-left rounded-lg border px-4 py-3 transition-all ${
                      surveyData.reason === reason 
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400' 
                        : 'border-gray-200 hover:border-purple-300 dark:border-gray-700 dark:hover:border-purple-600'
                    }`}
                  >
                    <div className="flex items-center">
                      <div 
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                          surveyData.reason === reason 
                            ? 'border-purple-600 bg-purple-600 dark:border-purple-400 dark:bg-purple-400' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {surveyData.reason === reason && (
                          <div className="h-2 w-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                        {reason}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              
              {errors.reason && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {errors.reason}
                </p>
              )}
            </div>
          )}

          {/* Page 2: Improvement options */}
          {currentPage === 1 && (
            <div className="animate-fadeIn">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                How can we improve?
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Select all areas where you think we could do better.
              </p>
              
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {improvementOptions.map((improvement) => (
                  <button 
                    key={improvement}
                    type="button"
                    onClick={() => handleImprovementToggle(improvement)}
                    className={`text-left rounded-lg border px-4 py-3 transition-all ${
                      surveyData.improvements.includes(improvement) 
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400' 
                        : 'border-gray-200 hover:border-purple-300 dark:border-gray-700 dark:hover:border-purple-600'
                    }`}
                  >
                    <div className="flex items-center">
                      <div 
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                          surveyData.improvements.includes(improvement) 
                            ? 'border-purple-600 bg-purple-600 dark:border-purple-400 dark:bg-purple-400' 
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {surveyData.improvements.includes(improvement) && (
                          <svg className="h-3 w-3 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                        {improvement}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              
              {errors.improvements && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {errors.improvements}
                </p>
              )}
            </div>
          )}

          {/* Page 3: Feedback text area */}
          {currentPage === 2 && (
            <div className="animate-fadeIn">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Your feedback matters
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Please share any additional thoughts or suggestions.
              </p>
              
              <div className="mt-4">
                <textarea
                  rows={5}
                  value={surveyData.feedback}
                  onChange={handleFeedbackChange}
                  placeholder="Please share your thoughts..."
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-purple-600 focus:ring-purple-600 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
                />
                
                {errors.feedback && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {errors.feedback}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Page 4: Final confirmation */}
          {currentPage === 3 && (
            <div className="animate-fadeIn">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {isButtonLoading ? 'Deleting Your Account...' : 'Final Confirmation'}
              </h2>
              
              {error ? (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                      <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isButtonLoading ? (
                <div className="mt-6 flex flex-col items-center justify-center space-y-4 text-center">
                  <div className="relative h-16 w-16">
                    <div className="absolute inset-0 h-full w-full animate-spin rounded-full border-4 border-gray-200 border-t-purple-600 dark:border-gray-700 dark:border-t-purple-500"></div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Please wait while we delete your account and data. You will be redirected to the homepage when the process is complete.
                  </p>
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Are you absolutely sure you want to delete your account? This action <span className="font-semibold">cannot</span> be undone.
                  </p>
                  
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800 dark:text-red-200">This will:</h3>
                        <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                          <ul className="list-disc space-y-1 pl-5">
                            <li>Delete your account permanently</li>
                            <li>Remove all your data from our servers</li>
                            <li>Cancel any active subscriptions</li>
                            <li>Log you out of all devices</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isButtonLoading}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isButtonLoading}
                      className="flex-1 rounded-lg border border-red-600 bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-70"
                    >
                      {isButtonLoading ? (
                        <div className="flex items-center justify-center">
                          <ButtonSpinner size="sm" />
                          <span className="ml-2">Deleting...</span>
                        </div>
                      ) : (
                        'Yes, Delete My Account'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation buttons - only show when not on final page and not loading */}
          {currentPage < 3 && (
            <div className="mt-8 flex justify-between">
              {currentPage > 0 ? (
                <button
                  type="button"
                  onClick={handlePrevPage}
                  disabled={isButtonLoading}
                  className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <svg className="mr-1 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 15.707a1 1 0 01-1.414 0L5.636 10.05a1.01 1.01 0 010-1.414l5.657-5.657a1 1 0 111.414 1.414L7.414 9.9l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back
                </button>
              ) : (
                <div></div>
              )}
              <button
                type="button"
                onClick={handleNextPage}
                disabled={!isCurrentPageValid() || isButtonLoading}
                className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:opacity-50 dark:bg-purple-600 dark:hover:bg-purple-700"
              >
                {isButtonLoading ? (
                  <div className="flex items-center">
                    <ButtonSpinner size="sm" />
                    <span className="ml-2">Next</span>
                  </div>
                ) : (
                  <>
                    Next
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 